<template>
  <!--
    ประวัติการทำรายการ (Log) ของสถานศึกษา — unit_audit.php · อ่านได้อย่างเดียว (แก้ไข/ลบไม่ได้)
    สถานศึกษา/สถาบัน: ดูของหน่วยงานตนเอง + ขอกู้คืนรายการที่ทำผิดพลาด
    admin = true: ทุกหน่วยงาน (กรองได้) + พิจารณาคำขอกู้คืน
    ใช้ผ่าน ref: open(tab?)
  -->
  <v-dialog v-model="dialog" max-width="1300" scrollable>
    <v-card>
      <v-toolbar dense dark flat color="blue-grey darken-2" class="flex-grow-0">
        <v-icon left>mdi-history</v-icon>
        <v-toolbar-title class="subtitle-1">
          ประวัติการทำรายการ (Log){{ admin ? " — สถานศึกษาทั้งหมด" : "" }}
        </v-toolbar-title>
        <v-spacer />
        <v-chip x-small color="white" outlined class="mr-2"><v-icon x-small left>mdi-lock</v-icon>แก้ไข/ลบไม่ได้</v-chip>
        <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <div class="d-flex flex-wrap align-center px-4 pt-3" style="gap:8px">
        <v-text-field v-if="admin" v-model="collegeCode" dense outlined hide-details clearable label="รหัสหน่วยงาน (ว่าง = ทั้งหมด)"
          style="max-width:230px" @change="reload" />
        <v-text-field v-model="dateFrom" type="date" dense outlined hide-details label="ตั้งแต่" style="max-width:170px" @change="reload" />
        <v-text-field v-model="dateTo" type="date" dense outlined hide-details label="ถึง" style="max-width:170px" @change="reload" />
        <v-btn small text color="primary" :loading="loading" @click="reload"><v-icon small left>mdi-refresh</v-icon>โหลดใหม่</v-btn>
      </div>
      <v-tabs v-model="tab" class="flex-grow-0 px-2" @change="reload">
        <v-tab>กิจกรรม</v-tab>
        <v-tab>การเปลี่ยนแปลงข้อมูล</v-tab>
        <v-tab>
          คำขอกู้คืน
          <v-chip v-if="pendingCount" x-small color="warning" dark class="ml-1">{{ pendingCount }}</v-chip>
        </v-tab>
      </v-tabs>
      <v-card-text style="height:68vh" class="pt-2">
        <v-tabs-items v-model="tab">
          <!-- กิจกรรม -->
          <v-tab-item>
            <v-data-table :headers="activityHeaders" :items="activity" :loading="loading" dense :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }" no-data-text="ยังไม่มีกิจกรรม">
              <template v-slot:[`item.created_at`]="{ item }">
                {{ item.created_at }}<div class="caption grey--text">{{ item.actor_name }} · {{ item.college_code }}</div>
              </template>
              <template v-slot:[`item.endpoint`]="{ item }">
                <div class="font-weight-medium">{{ endpointLabel(item.endpoint, item.action) }}</div>
                <div class="caption grey--text">{{ item.endpoint }}{{ item.action ? " · " + item.action : "" }}</div>
              </template>
              <template v-slot:[`item.summary`]="{ item }">
                <div class="caption" style="max-width:380px;white-space:normal;word-break:break-all">{{ summaryText(item.summary) }}</div>
              </template>
              <template v-slot:[`item.result_status`]="{ item }">
                <v-chip x-small dark :color="item.result_status === 'false' || item.http_status >= 400 ? 'error' : 'success'">
                  {{ item.result_status === "false" || item.http_status >= 400 ? "ไม่สำเร็จ" : "สำเร็จ" }}
                </v-chip>
                <div v-if="item.result_message" class="caption" style="max-width:220px;white-space:normal">{{ item.result_message }}</div>
              </template>
              <template v-slot:[`item.changes`]="{ item }">
                <v-btn v-if="+item.changes" x-small text color="primary" @click="showRequest(item.request_id)">{{ item.changes }} แถว</v-btn>
                <span v-else class="caption grey--text">-</span>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- การเปลี่ยนแปลงข้อมูล -->
          <v-tab-item>
            <div class="d-flex flex-wrap mb-2" style="gap:8px">
              <v-select v-model="tableName" :items="tableItems" dense outlined hide-details clearable label="ข้อมูล" style="max-width:260px" @change="loadChanges" />
              <v-text-field v-model="q" dense outlined hide-details clearable label="ค้นหาเลขบัตร/ชื่อ" style="max-width:240px" @keyup.enter="loadChanges" @click:clear="q = ''; loadChanges()" />
              <v-chip v-if="requestId" close small @click:close="requestId = ''; loadChanges()">เฉพาะคำขอ {{ requestId }}</v-chip>
            </div>
            <v-data-table :headers="changeHeaders" :items="changes" :loading="loading" dense :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }" no-data-text="ยังไม่มีการเปลี่ยนแปลง">
              <template v-slot:[`item.created_at`]="{ item }">
                {{ item.created_at }}<div class="caption grey--text">{{ item.actor_name }} · {{ item.actor_college || "-" }}</div>
              </template>
              <template v-slot:[`item.table_label`]="{ item }">
                <v-chip x-small dark :color="opColor(item.op)" class="mr-1">{{ opText(item.op) }}</v-chip>{{ item.table_label }}
                <div class="caption grey--text">{{ item.endpoint }}</div>
              </template>
              <template v-slot:[`item.person`]="{ item }">
                {{ item.person || "-" }}<div class="caption grey--text">{{ item.id_card || JSON.stringify(item.row_pk) }}</div>
              </template>
              <template v-slot:[`item.diff`]="{ item }">
                <div v-if="item.op === 'UPDATE'">
                  <div v-for="d in item.diff.slice(0, expanded[item.id] ? 999 : 4)" :key="d.col" class="caption">
                    {{ d.col }}: <span class="red--text text--darken-1">{{ show(d.old) }}</span> → <span class="success--text text--darken-2">{{ show(d.new) }}</span>
                  </div>
                  <a v-if="item.diff.length > 4" class="caption" @click="$set(expanded, item.id, !expanded[item.id])">
                    {{ expanded[item.id] ? "ย่อ" : `+ อีก ${item.diff.length - 4} รายการ` }}
                  </a>
                </div>
                <div v-else class="caption grey--text">{{ item.op === "INSERT" ? "สร้างแถวใหม่" : "ลบแถว (เก็บค่าเดิมไว้)" }}</div>
              </template>
              <template v-slot:[`item.restore`]="{ item }">
                <v-chip v-if="item.restore_status" x-small dark :color="statusColor(item.restore_status)">{{ statusText(item.restore_status) }}</v-chip>
                <v-btn v-else-if="!admin" x-small outlined color="warning darken-2" @click="askRestore(item)">ขอกู้คืน</v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- คำขอกู้คืน -->
          <v-tab-item>
            <v-data-table :headers="requestHeaders" :items="requests" :loading="loading" dense :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [25, 50, -1] }" no-data-text="ยังไม่มีคำขอกู้คืน">
              <template v-slot:[`item.requested_at`]="{ item }">
                {{ item.requested_at }}<div class="caption grey--text">{{ item.requested_by_name }} · {{ item.college_code }}</div>
              </template>
              <template v-slot:[`item.change`]="{ item }">
                <v-chip x-small dark :color="opColor(item.change.op)" class="mr-1">{{ opText(item.change.op) }}</v-chip>
                {{ item.change.table_label }} · {{ item.change.person || item.change.id_card || "" }}
                <div class="caption grey--text">รายการเมื่อ {{ item.change.created_at }}</div>
                <div v-for="d in item.change.diff.slice(0, 3)" :key="d.col" class="caption">
                  {{ d.col }}: {{ show(d.new) }} → <strong>{{ show(d.old) }}</strong> (ค่าที่จะคืน)
                </div>
              </template>
              <template v-slot:[`item.reason`]="{ item }">
                <div class="caption" style="max-width:240px;white-space:normal">{{ item.reason }}</div>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small dark :color="statusColor(item.status)">{{ statusText(item.status) }}</v-chip>
                <div v-if="item.reviewed_at" class="caption grey--text">{{ item.reviewed_at }} {{ item.reviewed_by_name }}</div>
                <div v-if="item.result_detail" class="caption">{{ item.result_detail }}</div>
                <div v-if="item.review_note" class="caption">หมายเหตุ: {{ item.review_note }}</div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <template v-if="admin && item.status === 'pending'">
                  <v-btn x-small color="success" class="mb-1" :loading="busy === item.id" @click="review(item, 'approve')">อนุมัติ/กู้คืน</v-btn>
                  <v-btn x-small outlined color="error" :loading="busy === item.id" @click="review(item, 'reject')">ไม่อนุมัติ</v-btn>
                </template>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>

    <v-dialog v-model="restoreDialog" max-width="560">
      <v-card v-if="restoreItem">
        <v-card-title class="subtitle-1">ขอกู้คืนรายการ</v-card-title>
        <v-card-text>
          <div class="body-2 mb-2">
            {{ opText(restoreItem.op) }} {{ restoreItem.table_label }} · {{ restoreItem.person || restoreItem.id_card }} · {{ restoreItem.created_at }}
          </div>
          <v-alert type="info" dense text class="caption">
            ส่งคำขอถึงส่วนกลาง — เมื่ออนุมัติ ระบบคืนค่าก่อนรายการนี้ (ถ้าข้อมูลถูกแก้ซ้ำภายหลัง ระบบจะไม่กู้คืนอัตโนมัติ)
          </v-alert>
          <v-textarea v-model="restoreReason" outlined dense rows="3" counter="500" label="เหตุผลที่ขอกู้คืน (อย่างน้อย 10 ตัวอักษร) *" />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="restoreDialog = false">ยกเลิก</v-btn>
          <v-spacer />
          <v-btn color="warning darken-2" dark :loading="!!busy" @click="submitRestore">ส่งคำขอกู้คืน</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="6000">{{ snack.text }}</v-snackbar>
  </v-dialog>
</template>

<script>
const API = "unit_audit.php";
const ENDPOINTS = {
  "govem_health.php": "ตรวจ/ปรับปรุงข้อมูลพนักงานราชการ",
  "government_employee.update.php": "แก้ไขข้อมูลกรอบอัตรากำลัง",
  "personnel_temporary.sync_single.php": "ซิงก์ทะเบียนบุคคลตามกรอบ",
  "govem_data_quality.php": "แก้รายชื่อซ้ำ/ค้าง",
  "gov_em_assessment.php": "ผลการประเมิน",
  "gov_em_attendance.php": "ข้อมูลการลา",
  "gov_em_salary_history.insert.php": "เพิ่มประวัติเงินเดือน",
  "gov_em_salary_history.update.php": "แก้ไขประวัติเงินเดือน",
  "government_employee_transfer.request.php": "ยื่นคำขอโยกย้าย",
  "government_employee_transfer.confirm.php": "ยืนยันคำขอโยกย้าย",
  "government_employee_transfer.cancel.php": "ยกเลิกคำขอโยกย้าย",
  "government_employee_resign.php": "พ้นสภาพ",
  "personnel_temporary_govem.resign.php": "บันทึกการลาออก",
  "govem_compensation_college.php": "แบบรับรองการเลื่อนค่าตอบแทน",
  "unit_audit.php": "ขอกู้คืนรายการ"
};

export default {
  name: "UnitAuditLog",
  props: { admin: { type: Boolean, default: false } },
  data() {
    return {
      ApiKey: "HRvec2021",
      dialog: false,
      tab: 0,
      loading: false,
      busy: null,
      collegeCode: "",
      dateFrom: "",
      dateTo: "",
      activity: [],
      changes: [],
      requests: [],
      tableItems: [],
      tableName: null,
      q: "",
      requestId: "",
      expanded: {},
      restoreDialog: false,
      restoreItem: null,
      restoreReason: "",
      snack: { show: false, text: "", color: "success" },
      activityHeaders: [
        { text: "เมื่อ / ผู้ทำรายการ", value: "created_at", width: 190 },
        { text: "รายการ", value: "endpoint" },
        { text: "ข้อมูลที่ส่ง", value: "summary", sortable: false },
        { text: "ผล", value: "result_status", width: 160 },
        { text: "แถวที่เปลี่ยน", value: "changes", width: 100 }
      ],
      changeHeaders: [
        { text: "เมื่อ / ผู้ทำรายการ", value: "created_at", width: 190 },
        { text: "ข้อมูล", value: "table_label", width: 230 },
        { text: "บุคคล/แถว", value: "person", width: 190 },
        { text: "ค่าเดิม → ค่าใหม่", value: "diff", sortable: false },
        { text: "", value: "restore", sortable: false, width: 100 }
      ],
      requestHeaders: [
        { text: "ขอเมื่อ", value: "requested_at", width: 190 },
        { text: "รายการที่ขอกู้คืน", value: "change", sortable: false },
        { text: "เหตุผล", value: "reason", sortable: false },
        { text: "สถานะ", value: "status", width: 200 },
        { text: "", value: "actions", sortable: false, width: 110 }
      ]
    };
  },
  computed: {
    pendingCount() {
      return this.requests.filter(r => r.status === "pending").length;
    }
  },
  methods: {
    async api(payload) {
      try {
        const res = await this.$http.post(API, {
          ApiKey: this.ApiKey,
          college_code: this.admin ? this.collegeCode || undefined : undefined,
          date_from: this.dateFrom || undefined,
          date_to: this.dateTo || undefined,
          ...payload
        });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    async open(tab = 0) {
      this.dialog = true;
      this.tab = tab;
      if (!this.tableItems.length) {
        try {
          this.tableItems = (await this.api({ action: "tables" })).tables.map(t => ({ text: t.label, value: t.table }));
        } catch (e) {
          this.tableItems = [];
        }
      }
      await this.reload();
      if (tab !== 2) this.loadRequests();
    },
    async reload() {
      if (this.tab === 0) return this.loadActivity();
      if (this.tab === 1) return this.loadChanges();
      return this.loadRequests();
    },
    async run(fn) {
      this.loading = true;
      try {
        await fn();
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loading = false;
      }
    },
    loadActivity() {
      return this.run(async () => {
        this.activity = (await this.api({ action: "activity" })).activity;
      });
    },
    loadChanges() {
      return this.run(async () => {
        this.changes = (await this.api({ action: "changes", table_name: this.tableName || undefined, q: this.q || undefined,
          request_id: this.requestId || undefined })).changes;
      });
    },
    loadRequests() {
      return this.run(async () => {
        this.requests = (await this.api({ action: "requests" })).requests;
      });
    },
    showRequest(requestId) {
      this.requestId = requestId;
      this.tab = 1;
      this.loadChanges();
    },
    endpointLabel(ep, action) {
      return ENDPOINTS[ep] || ep.replace(/\.php$/, "").replace(/[._]/g, " ") + (action ? ` (${action})` : "");
    },
    summaryText(json) {
      try {
        const o = JSON.parse(json || "{}");
        return Object.entries(o).map(([k, v]) => `${k}=${v}`).join(" · ") || "-";
      } catch (e) {
        return json || "-";
      }
    },
    show(v) {
      return v === null || v === undefined || v === "" ? "(ว่าง)" : String(v);
    },
    opText(op) {
      return { INSERT: "เพิ่ม", UPDATE: "แก้ไข", DELETE: "ลบ" }[op] || op;
    },
    opColor(op) {
      return { INSERT: "success", UPDATE: "primary", DELETE: "error" }[op] || "grey";
    },
    statusText(s) {
      return { pending: "รอส่วนกลางพิจารณา", approved: "กู้คืนแล้ว", rejected: "ไม่อนุมัติ", failed: "กู้คืนไม่ได้" }[s] || s;
    },
    statusColor(s) {
      return { pending: "warning", approved: "success", rejected: "grey", failed: "error" }[s] || "grey";
    },
    askRestore(item) {
      this.restoreItem = item;
      this.restoreReason = "";
      this.restoreDialog = true;
    },
    async submitRestore() {
      this.busy = "restore";
      try {
        await this.api({ action: "request_restore", change_log_id: this.restoreItem.id, reason: this.restoreReason });
        this.restoreDialog = false;
        this.notify("ส่งคำขอกู้คืนถึงส่วนกลางแล้ว");
        await Promise.all([this.loadChanges(), this.loadRequests()]);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    },
    async review(item, decision) {
      let note = "";
      if (decision === "reject") {
        note = prompt("เหตุผลที่ไม่อนุมัติ") || "";
        if (note.trim().length < 5) return;
      } else if (!confirm("อนุมัติและกู้คืนรายการนี้ทันที?")) {
        return;
      }
      this.busy = item.id;
      try {
        const r = await this.api({ action: "review", id: item.id, decision, note });
        this.notify(r.result === "approved" ? "กู้คืนเรียบร้อย — " + (r.detail || "") : r.result === "failed" ? "กู้คืนไม่ได้: " + r.detail : "บันทึกผลแล้ว",
          r.result === "failed" ? "error" : "success");
        await this.loadRequests();
        this.$emit("restored");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    }
  }
};
</script>
